import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  purgeOfflineCache,
  readOfflineMeta,
  restoreOfflineCache,
  restoreOfflineSession,
  startOfflineCache,
  stopOfflineCache,
} from './persister';
import { isOfflineCacheSupported } from './cacheDb';
import { purgeApiCache, watchKeepMailForServiceWorker, watchSessionForServiceWorker } from './apiCache';
import { useKeepMailOnDevice } from './keepOnDevice';

/**
 * Hydration and the persister's lifetime, both driven from the `['session']`
 * query rather than from `useSession`.
 *
 * That is deliberate. To make a cold start with no connection render *mail*
 * instead of "temporarily unavailable", the session snapshot has to be in the
 * cache before `SessionProvider` mounts and asks the server — so this sits
 * above it, and cannot consume its context. Watching the query cache gives the
 * same information from the other side of the same object.
 *
 * ## The rules
 *
 * 1. **Offline at boot** → put the session snapshot back, then the mail. With
 *    a network, `GET /auth/session` is the authority and nothing pre-empts it.
 * 2. **A session resolves for a different owner than the cache holds** → purge
 *    the cache before restoring anything. The sign-out purge is the main
 *    defence; this is the one that covers a sign-out that never completed.
 * 3. **The session goes away** → stop persisting. `teardownSession` does the
 *    deleting; this only makes sure nothing writes over its shoulder.
 * 4. **"Keep mail on this device" is turned off** (ADR 0060) → stop
 *    persisting, delete both on-disk copies, and *do not touch the query
 *    client*. The mail on screen lives in memory; clearing it would blank the
 *    list of someone who was reading it, which is not what they asked for.
 *    Turning it back on re-adopts, and the persister's opening sweep over
 *    everything already in the cache repopulates the disk from memory.
 */
export function OfflineCacheProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [keep] = useKeepMailOnDevice();
  // Gate the first paint only in the case that needs it: a cold start with no
  // connection, where rendering before hydration means rendering the
  // "unavailable" screen and then yanking it away. With the setting off there
  // is nothing to hydrate *from*, so there is nothing to wait for.
  const [ready, setReady] = useState(
    () =>
      !isOfflineCacheSupported() ||
      !keep ||
      (typeof navigator !== 'undefined' && navigator.onLine),
  );

  // The service worker's own cache of API reads is the second copy of this
  // user's mail on this disk, and the setting has to cover it too. Telling the
  // worker is cheap and has to be repeated (a stopped worker forgets); the
  // purge runs on every start while the setting is off, which is what clears
  // anything a worker cached before it had been told.
  useEffect(() => {
    const stop = watchKeepMailForServiceWorker(() => keep);
    if (!keep) void purgeApiCache();
    return stop;
  }, [keep]);

  // Sign-out's purge also tells the worker to stop using its API cache at all
  // (#35); a signed-in page lifts that, on boot and after each sign-in. Keyed
  // on `keep` too: the start-up purge above while the setting is off sends
  // the same stop, so turning the setting back on has to lift it again.
  useEffect(() => watchSessionForServiceWorker(client), [client, keep]);

  useEffect(() => {
    let cancelled = false;

    async function hydrate(): Promise<void> {
      try {
        if (keep && typeof navigator !== 'undefined' && !navigator.onLine) {
          const session = await restoreOfflineSession(client);
          if (!cancelled && session !== undefined) {
            await restoreOfflineCache(client, session.account.id);
            // The snapshot is a stand-in, not an answer. The moment there is a
            // network, ask the server who this is — `staleTime: Infinity` on
            // the session query means nothing else will.
            const revalidate = (): void => {
              window.removeEventListener('online', revalidate);
              void client.invalidateQueries({ queryKey: queryKeys.session() });
            };
            window.addEventListener('online', revalidate);
          }
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [client, keep]);

  useEffect(() => {
    if (!ready) return;

    // Off: nothing subscribes, and what is already on disk goes now. The query
    // client is deliberately untouched — see rule 4.
    if (!keep) {
      stopOfflineCache();
      void purgeOfflineCache();
      // The composer's local draft copies are message text too (#180): off
      // means none of it stays on this disk.
      void purgeLocalDrafts();
      return;
    }

    let owner: string | undefined;
    let cancelled = false;

    const apply = (): void => {
      const session = client.getQueryData(queryKeys.session()) as
        | { account?: { id?: string; email?: string } }
        | undefined;
      const id = session?.account?.id;
      const email = session?.account?.email;

      if (id === undefined || email === undefined) {
        if (owner !== undefined) {
          owner = undefined;
          stopOfflineCache();
        }
        return;
      }
      if (id === owner) return;
      owner = id;
      void adopt(client, id, email, () => cancelled);
    };

    apply();
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === 'session' && event.query.queryKey.length === 1) apply();
    });

    return () => {
      cancelled = true;
      unsubscribe();
      stopOfflineCache();
    };
  }, [client, ready, keep]);

  if (!ready) return null;
  return <>{children}</>;
}

async function adopt(
  client: QueryClient,
  owner: string,
  email: string,
  cancelled: () => boolean,
): Promise<void> {
  const meta = await readOfflineMeta();
  if (cancelled()) return;
  if (meta !== undefined && meta.owner !== owner) {
    // Somebody else's mail is on this disk. A completed sign-out would have
    // deleted it; this one did not complete, so delete it now and start clean.
    await purgeOfflineCache();
    await purgeLocalDrafts();
    if (cancelled()) return;
  } else {
    await restoreOfflineCache(client, owner);
    if (cancelled()) return;
  }
  startOfflineCache(client, { owner, email });
}

/**
 * The composer's local draft copies (#180) go wherever the offline cache goes
 * (ADR 0060). Loaded on demand: the store is the composer's, not the first
 * paint's.
 */
async function purgeLocalDrafts(): Promise<void> {
  const { purgeDraftStore } = await import('@/routes/mail/draftDb');
  await purgeDraftStore();
}
